import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, ShieldCheck, Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";

const WA_URL = "https://wa.me/447481626260?text=" + encodeURIComponent("Hi Prime Build, I'd like to discuss a project.");

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "Renovation",
    budget_range: "£100k – £250k",
    timeline: "3–6 months",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within one business day.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us — Get a Free Quote"
        description="Get in touch with Prime Build Design LTD. Free 45-min consultation for residential builds, renovations & extensions across London. Call 07481 626 260 or request a quote online."
        path="/contact"
      />

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28">
          <div className="overline mb-6">Get in touch</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            Let's talk about<br /> your home.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-2xl">
            Tell us about your project. We'll reply within one business day with next steps and
            availability.
          </p>

          {/* Conversion bar — fast-action options + reply promise */}
          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-slate-200 max-w-3xl" data-testid="contact-quick-actions">
            <a
              href="tel:+447481626260"
              data-testid="contact-quick-call"
              className="bg-white p-5 hover:bg-slate-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#C5A059]" />
                <div className="text-xs font-medium tracking-[0.16em] uppercase text-slate-500">Call now</div>
              </div>
              <div className="mt-2 font-display text-lg font-semibold text-slate-900 group-hover:text-[#C5A059] transition-colors">
                07481 626 260
              </div>
            </a>
            <a
              href={WA_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-quick-wa"
              className="bg-white p-5 hover:bg-slate-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <MessageCircle size={18} className="text-[#25D366]" />
                <div className="text-xs font-medium tracking-[0.16em] uppercase text-slate-500">WhatsApp</div>
              </div>
              <div className="mt-2 font-display text-lg font-semibold text-slate-900 group-hover:text-[#C5A059] transition-colors">
                Chat now
              </div>
            </a>
            <div className="bg-white p-5" data-testid="contact-quick-promise">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#C5A059]" />
                <div className="text-xs font-medium tracking-[0.16em] uppercase text-slate-500">Promise</div>
              </div>
              <div className="mt-2 font-display text-lg font-semibold text-slate-900">
                Reply within 24h
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-10">
            <div>
              <div className="overline mb-3">Visit</div>
              <div className="flex gap-3 items-start text-slate-700">
                <MapPin size={20} className="text-[#C5A059] mt-1" />
                <div>45 Bridge Road<br />Wembley Park<br />HA9 9AG · London</div>
              </div>
            </div>
            <div>
              <div className="overline mb-3">Call</div>
              <div className="flex gap-3 items-start text-slate-700">
                <Phone size={20} className="text-[#C5A059] mt-1" />
                <div>
                  <a href="tel:+447481626260" className="hover:underline" data-testid="contact-phone">07481 626 260</a>
                  <br /><span className="text-sm text-slate-500">Mon–Fri, 9:00–18:00</span>
                </div>
              </div>
            </div>
            <div>
              <div className="overline mb-3">Email</div>
              <div className="flex gap-3 items-start text-slate-700">
                <Mail size={20} className="text-[#C5A059] mt-1" />
                <a href="mailto:primebuilddesignltd9@gmail.com" className="hover:underline break-all" data-testid="contact-email">primebuilddesignltd9@gmail.com</a>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-8 mt-10">
              <div className="overline text-[#C5A059] mb-3">Why Prime Build?</div>
              <ul className="space-y-3 text-sm text-slate-300 leading-relaxed">
                <li>· Free 45-min discovery consultation</li>
                <li>· Fixed-price contracts with weekly reports</li>
                <li>· 10-year structural guarantee</li>
                <li>· In-house joinery, MEP & finishing teams</li>
              </ul>
            </div>

            {/* Social-proof card */}
            <div className="border border-slate-200 p-6" data-testid="contact-social-proof">
              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="text-[#C5A059] fill-[#C5A059]" strokeWidth={1.5} />
                ))}
                <span className="overline text-slate-500 ml-1">4.9 / 62 reviews</span>
              </div>
              <p className="mt-4 font-display text-lg text-slate-900 leading-snug">
                "Their team understood our brief immediately — modern lines but a home that feels warm."
              </p>
              <div className="mt-4 text-xs tracking-[0.16em] uppercase text-slate-500">
                Priya R. · Surrey New Build
              </div>
              <div className="mt-5 flex items-center gap-2 pt-5 border-t border-slate-200">
                <ShieldCheck size={16} className="text-[#C5A059]" />
                <span className="text-xs text-slate-700">Insured · 10-yr guarantee · UK registered</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            {submitted ? (
              <div className="border border-slate-200 p-12 text-center" data-testid="contact-success">
                <div className="overline mb-4 text-[#C5A059]">Thank you</div>
                <h3 className="font-display text-3xl font-bold text-slate-900">Your enquiry is in.</h3>
                <p className="mt-4 text-slate-600 max-w-md mx-auto">
                  We've logged your request. A project lead will be in touch within one business day
                  to schedule your discovery call.
                </p>
                <button
                  data-testid="contact-submit-another"
                  className="btn-ghost mt-8"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ ...form, name: "", email: "", phone: "", message: "" });
                  }}
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-slate-200 p-8 md:p-10" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="overline">Your name *</label>
                    <input data-testid="contact-name" className="field mt-2" required value={form.name} onChange={update("name")} />
                  </div>
                  <div>
                    <label className="overline">Email *</label>
                    <input data-testid="contact-email-input" type="email" className="field mt-2" required value={form.email} onChange={update("email")} />
                  </div>
                  <div>
                    <label className="overline">Phone *</label>
                    <input data-testid="contact-phone-input" className="field mt-2" required value={form.phone} onChange={update("phone")} />
                  </div>
                  <div>
                    <label className="overline">Project type</label>
                    <select data-testid="contact-project-type" className="field mt-2" value={form.project_type} onChange={update("project_type")}>
                      <option>New Build</option>
                      <option>Renovation</option>
                      <option>Extension</option>
                      <option>Design-Build</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Budget range</label>
                    <select data-testid="contact-budget" className="field mt-2" value={form.budget_range} onChange={update("budget_range")}>
                      <option>Under £100k</option>
                      <option>£100k – £250k</option>
                      <option>£250k – £500k</option>
                      <option>£500k – £1M</option>
                      <option>£1M+</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline">Timeline</label>
                    <select data-testid="contact-timeline" className="field mt-2" value={form.timeline} onChange={update("timeline")}>
                      <option>ASAP</option>
                      <option>1–3 months</option>
                      <option>3–6 months</option>
                      <option>6–12 months</option>
                      <option>Just exploring</option>
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="overline">Tell us about your project *</label>
                  <textarea
                    data-testid="contact-message"
                    className="field mt-2"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Address, scope, key requirements, anything else you'd like us to know…"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={sending}
                  className="btn-primary mt-6"
                >
                  {sending ? "Sending…" : (<>Send enquiry <Send size={16} /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="overline mb-4">Find us</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
            45 Bridge Road, Wembley Park
          </h2>
          <div className="border border-slate-200 overflow-hidden" data-testid="contact-map">
            <iframe
              title="Prime Build Design office location"
              src="https://www.google.com/maps?q=45+Bridge+Road,+Wembley+Park,+HA9+9AG&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}
